import { log } from 'log';

/**
 * Create script DOM element with provided params
 * @function
 * @param [params.src] {string} script src
 * @param [params.code] {string} script inner code
 * @param [params.attrs] {Object} script attributes
 * @returns {Node}
 * @example createScriptElement({ code: 'console.log("script")', attrs: { 'async': true } })
 */
export const createScriptElement = ({ src, context, code, attrs = {} }) => {
    const script = document.createElement('script');

    script.type = 'text/javascript';

    for (const property in attrs) {
        script.setAttribute(property, attrs[property]);
    }

    if (code) {
        if (context) {
            script.appendChild(document.createTextNode([
                `//# sourceURL=vm://internal/${encodeURIComponent(context)}`,
                `/* ${context} */`,
                code
            ].join('\n')));
        } else {
            script.appendChild(document.createTextNode(code));
        }
    }

    if (src) {
        script.src = src;
    }

    return script;
};

/**
 * Appends script to the head and removes it after that;
 * @function
 * @param [params.src] {string} script src
 * @param [params.code] {string} script inner code
 * @param [params.attrs] {Object} script attributes
 * @example evalJavaScript({ code: 'console.log("script")', attrs: { 'async': true } })
 */
export const evalJavaScript = (options) => {
    const head = document.querySelector('head');
    const script = createScriptElement(options);

    head.append(script);
    script.remove();
};

/**
 * Finds all script elements within a provided HTML;
 * @function
 * @param [html] {string} HTML as a simple string
 * @example findScriptElements('<div><script>console.log("test")</script></div>')
 */
export const findScriptElements = (html) => {
    try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const scripts = doc.querySelectorAll('script');

        return [...scripts].map(({ textContent, attributes }) => ({
            code: textContent,
            attrs: Object.assign({},
                ...Array.from(attributes, ({ name, value }) => ({ [name]: value }))
            )
        }));

    } catch (e) {
        log('error', e);
    }

    return [];
};

/**
 * Runs all script found within a provided HTML;
 * @function
 * @param [html] {string} HTML as a simple string
 * @param [context] {string} Context to be commented in the script
 * @example findScriptElements('<div><script>console.log("test")</script></div>')
 */
export const evalAllScriptsInHtml = (html, context) => {
    const scripts = findScriptElements(html);

    scripts.forEach((script) => evalJavaScript({ ...script, context }));
};

/**
 * Checks whether provided string is a valid HTML
 * @function
 * @param [html] {string} HTML as a simple string
 * @returns {boolean}
 * @example isHtml('<div><script>console.log("test")</script></div>')
 */
export const isHtml = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    return Array.from(doc.body.childNodes).some(node => node.nodeType === 1);
};

/**
 * Insert HTML and return a function that removes inserted elements from DOM.
 * @function
 * @param [params.selector] {string} selector of element where HTML will be inserted in
 * @param [params.html] {string} HTML to be inserted
 * @param [params.position] {string} position of the appended html (beforeend | afterbegin)
 * @param [params.tagName] {string} Tag name for comments
 * @param [params.wrapperComment] {string} Comment to the provided HTML
 */
export const insertTrackableHTML = ({ selector, html, position = 'beforeend', tagName, wrapperComment }) => {
    const elements = [];
    const el = document.querySelector(selector);
    const renderer = document.createElement('div');
    const beforeComment = document.createComment(wrapperComment ? ` ${wrapperComment} ` : ` Tag name is ${tagName} `);
    const afterComment = document.createComment(wrapperComment ? ` End ${wrapperComment} ` : ` End tag name is ${tagName} `);

    renderer.innerHTML = html;

    if (position === 'beforeend' || wrapperComment) {
        el.append(beforeComment);
    }

    Array.from(renderer.children).forEach((element) => {
        elements.push(element);
        el.insertAdjacentElement(position, element);
    });

    if (wrapperComment) {
        el.append(afterComment);
    }

    if (position === 'afterbegin' && !wrapperComment) {
        el.prepend(beforeComment);
    }

    evalAllScriptsInHtml(html, `insertTrackableHTML::${tagName || 'unknown'}`);

    return () => {
        for (const element of elements) {
            element.remove();
        }
        beforeComment.remove();
        afterComment.remove();
    };
};

/**
 * @function
 * @param element {HTMLElement}
 * @returns boolean
 */
export const isMirrorModeElement = (element) => getComputedStyle(element).direction === 'rtl';

/**
 * @function
 * @param {HTMLElement} element
 * @param {number} percentage from 0 to 1 where 1 is 100%
 * @returns {boolean}
 */
export const isInViewportByPercentage = (element, percentage) => {
    if (!element) {
        return false;
    }

    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;

    return !(
        Math.floor(100 - (rect.top >= 0 ? 0 : rect.top) / -rect.height * 100) < 100 * percentage ||
        Math.floor(100 - (rect.bottom - windowHeight) / rect.height * 100) < 100 * percentage
    );
};

/**
 * Trigger change event on passed input with given value
 * @function
 * @param {Object} params
 * @param {string | number} params.value
 * @param {HTMLInputElement | null} params.input
 */
export const changeInputValue = ({ value, input }) => {
    if (!input) {
        return;
    }

    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;

    nativeInputValueSetter?.call(input, value);
    input.dispatchEvent(new InputEvent('change', { bubbles: true }));
};

export const isVoidElement = (tagName) => [
    'area',
    'base',
    'br',
    'col',
    'embed',
    'hr',
    'img',
    'input',
    'link',
    'meta',
    'param',
    'source',
    'track',
    'wbr'
].includes(tagName);

export const hideKeyboard = () => {
    if (document.activeElement?.blur && document.activeElement.nodeName !== 'BODY') {
        document.activeElement.blur();
    }
};
